import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Job } from '../../../interfaces/job';
import { Table } from '../../../../table/Table';
import { TableRow } from '../../../../table/interfaces/table';
import { ListComponentBaseProps } from '../../../../entity-list/interface';
import { jobTableColumns } from './JobsColumns';
import { useAnalyticsTracker } from '../../../../tracking/hooks/useAnalyticsTracker';
import { JobsTrackers } from '../../../jobs-trackers';

export interface JobsTableProps extends ListComponentBaseProps<Job[]> {
  withPipelineDetails: boolean;
}

export function JobsTable({ data: jobs, withPipelineDetails, tableProps }: JobsTableProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { eventTrack } = useAnalyticsTracker();

  const isPipelineJobsHistoryPage = useMemo(() => {
    return location.pathname.includes('/job-history');
  }, [location.pathname]);

  const columns = useMemo(() => {
    let jobColumns = jobTableColumns(withPipelineDetails);
    const isAnyLoadOnlyJob = jobs.some(job => job.isLoadOnly);

    if (isPipelineJobsHistoryPage && isAnyLoadOnlyJob) {
      jobColumns = jobColumns.filter(job => job.id !== 'ingested');
    }

    return jobColumns;
  }, [withPipelineDetails, isPipelineJobsHistoryPage]);

  const onNavigationClick = (job: Job) => {
    if (withPipelineDetails) {
      eventTrack({
        action: JobsTrackers.JOB_TRACKER_JOBS_TABLE_JOB_WITH_PIPELINE_DETAILS_CLICK,
        properties: {
          pipelineId: job.pipeline?.id,
          jobId: job.jobId
        }
      });

      navigate(`/pipeline/edge/${job.pipeline?.id}/job-history/${job.jobId}`);
    } else {
      eventTrack({
        action: JobsTrackers.JOB_TRACKER_JOBS_TABLE_JOB_CLICK,
        properties: {
          pipelineId: job.pipeline?.id,
          jobId: job.jobId
        }
      });

      const path = `${location.pathname}/${job.jobId}`;
      navigate(path);
    }
  };

  const rows: TableRow<Job>[] = useMemo(() => {
    return jobs.map(job => ({
      data: job,
      tabIndex: 0,
      id: job.jobId,
      disabled: job.isDeletedIntegration || job.isSkipped,
      onClick: onNavigationClick
    }));
  }, [jobs]);

  return <Table columns={columns} rows={rows} {...tableProps} forceFullWidth />;
}
